import { FilterDescriptor, State, process } from '@progress/kendo-data-query';
import { DropDownListChangeEvent } from '@progress/kendo-react-dropdowns';
import {
  GridDataStateChangeEvent,
  GridHeaderSelectionChangeEvent,
  GridPagerSettings,
  GridSelectionChangeEvent,
} from '@progress/kendo-react-grid';
import { GridSelectableSettings } from '@progress/kendo-react-grid/dist/npm/interfaces/GridSelectableSettings';
import { AxiosResponse } from 'axios';
import { Dispatch, FC, SetStateAction, useCallback, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { useMemberCategories } from 'src/assets/static/view_department/useDepartmentCategories';
import { useMemberColumn } from 'src/assets/static/view_department/useMemberColumn';
import GridWrapper from 'src/components/GridWrapper';
import useViewDepartment from 'src/hooks/useViewDepartments';
import useWindowSize from 'src/hooks/useWindowSize';
import { BulkUpdateMemberRequest, Department } from 'src/types/department';
import { modifyFilters } from 'src/utils/requestInterceptor';
import { initialDepartmentState } from '../ViewDepartmentContext';
import { viewDepartmentFilters } from '../ViewDepartmentFilters';
import { IAssignMember } from './AssignMemberTable';
import BulkUpdateTableToolbar from './BulkUpdateTableToolbar';

const selectable: GridSelectableSettings = {
  enabled: false,
  drag: false,
  cell: false,
};

const gridPagerSettings: GridPagerSettings = {
  buttonCount: 1,
  pageSizes: [10, 20, 50, 100, 500],
};

const defaultProps = {
  tableTextId: 'Members',
  DataItemKey: 'rowId',
  sortable: true,
  resizable: true,
  showColumnMenu: true,
  selectable: selectable,
  pageable: gridPagerSettings,
} as const;

const initialUnassignMemberDataState: State = {
  skip: 0,
  take: 500,
  sort: [],
  filter: {
    logic: 'and',
    filters: [],
  },
};

interface UnassignMemberTableProps {
  handlePaneClose: () => void;
  selectedDepartments: Department[];
  unAssignMember: IAssignMember[];
  setUnAssignMember: Dispatch<SetStateAction<IAssignMember[]>>;
  bulkUpdateMember: (params: BulkUpdateMemberRequest) => Promise<AxiosResponse<any, any>>;
  isBulkUpdateMemberLoading: boolean;
}

const UnassignMemberTable: FC<UnassignMemberTableProps> = ({
  handlePaneClose,
  selectedDepartments,
  unAssignMember,
  setUnAssignMember,
  bulkUpdateMember,
  isBulkUpdateMemberLoading,
}) => {
  const { height } = useWindowSize();
  const dropDownList = useMemberCategories();
  const { keepSelectedRows, t, setDepartmentsDataState } = useViewDepartment();
  const assignDepartmentColumns = useMemberColumn();

  const [state, setState] = useState(initialUnassignMemberDataState);
  const [selectionFilter, setSelectionFilter] = useState(dropDownList[1]);

  const selectedRows = useMemo(
    () => unAssignMember.filter((member) => member.selected),
    [unAssignMember]
  );

  const getMemberFilters = useCallback(
    (state: State) => {
      const filters: FilterDescriptor[] = modifyFilters(state.filter, undefined, false);
      return [...filters, ...(viewDepartmentFilters[selectionFilter.value]?.filters || [])];
    },
    [selectionFilter.value]
  );

  const { data: processedMembers, total } = useMemo(
    () =>
      process(unAssignMember, {
        ...state,
        filter: {
          logic: 'and',
          filters: getMemberFilters(state),
        },
      }) as {
        data: IAssignMember[];
        total: number;
      },
    [getMemberFilters, state, unAssignMember]
  );

  const onCheckbookSelectionChange = useCallback(
    (event: GridSelectionChangeEvent) => {
      setUnAssignMember((prev) => {
        const prevItems = [...prev].map((item) =>
          item.rowId === event?.dataItem.rowId ? { ...item, selected: !item.selected } : item
        );
        return prevItems;
      });
    },
    [setUnAssignMember]
  );

  const changeDataState = useCallback(
    (e: GridDataStateChangeEvent) => {
      setUnAssignMember((prev) => prev?.map((row) => ({ ...row, selected: false })));
      setState(e.dataState);
    },
    [setUnAssignMember]
  );

  const onHeaderSelectionChange = useCallback(
    (event: GridHeaderSelectionChangeEvent) => {
      const checkboxElement: any = event?.syntheticEvent?.target;
      const { checked } = checkboxElement;
      setUnAssignMember((prev) => prev?.map((row) => ({ ...row, selected: checked })));
    },
    [setUnAssignMember]
  );

  const handleClearSelection = useCallback(() => {
    setUnAssignMember((prev) => prev?.map((row) => ({ ...row, selected: false })));
  }, [setUnAssignMember]);

  const handleFilterChange = useCallback(
    (e: DropDownListChangeEvent) => {
      setUnAssignMember((prev) => prev?.map((row) => ({ ...row, selected: false })));
      setSelectionFilter(e.value);
    },
    [setUnAssignMember]
  );

  const handleUnassignMember = useCallback(async () => {
    const departments = selectedDepartments.map((department) => department.departmentId);
    const clients = processedMembers
      ?.filter((member) => member.selected)
      ?.map((member) => member.clientId);

    try {
      await bulkUpdateMember({ departments, clients, assign: false });
      toast.success(t('Members unassigned from departments successfully'));
      keepSelectedRows();
      handlePaneClose();
      setDepartmentsDataState(initialDepartmentState);
    } catch (error) {
      toast.error(t('Failed to unassigned members to departments'));
    }
  }, [
    bulkUpdateMember,
    handlePaneClose,
    processedMembers,
    keepSelectedRows,
    selectedDepartments,
    setDepartmentsDataState,
    t,
  ]);

  const gridToolbarChildren = useMemo(
    () => (
      <BulkUpdateTableToolbar
        isAssign={false}
        isMember={true}
        selectionFilter={selectionFilter}
        dropDownList={dropDownList}
        handleClearSelection={handleClearSelection}
        selectedRows={selectedRows}
        handleFilterChange={handleFilterChange}
        handleUpdate={handleUnassignMember}
        selectedDepartment={selectedDepartments}
        isLoading={isBulkUpdateMemberLoading}
      />
    ),
    [
      selectionFilter,
      dropDownList,
      handleClearSelection,
      selectedRows,
      handleFilterChange,
      handleUnassignMember,
      selectedDepartments,
      isBulkUpdateMemberLoading,
    ]
  );

  return (
    <main className="p-[10px] bg-[#ffffff]">
      <GridWrapper
        id="assignMemberTable"
        columns={assignDepartmentColumns}
        dataSource={processedMembers}
        dataState={state}
        total={total}
        height={`${height - 320}px`}
        gridToolbarChildren={gridToolbarChildren}
        onDataStateChange={changeDataState}
        {...defaultProps}
        onHeaderSelectionChange={onHeaderSelectionChange}
        onSelectionChange={onCheckbookSelectionChange}
        noRecordText={t('No records to display')}
        loading={isBulkUpdateMemberLoading}
        hideScrollBar
      />
    </main>
  );
};

export default UnassignMemberTable;
